import React, { useState, useEffect } from 'react';
import { graphqlOperation } from '@aws-amplify/api-graphql';
import { generateClient } from '@aws-amplify/api';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { submitPropertyTransaction } from '../../graphql/mutations';
import './SubmissionModal.css';

const client = generateClient();

function SubmissionModal({ isOpen, onClose, userEmail, initialLocation }) {
  const { user } = useAuthenticator();

  const [formData, setFormData] = useState({
    latitude: '',
    longitude: '',
    price: '',
    date: '',
    property_type: 'Detached',
    total_floor_area: '',
    tenure: '',
    new_build: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Update form data when initialLocation changes
  useEffect(() => {
    if (initialLocation) {
      setFormData(prev => ({
        ...prev,
        latitude: initialLocation.lat.toString(),
        longitude: initialLocation.lon.toString()
      }));
    }
  }, [initialLocation]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };


  const validateForm = () => {
    const required = ['latitude', 'longitude', 'price', 'date', 'property_type'];
    for (const field of required) {
      if (!formData[field]) {
        alert(`Please fill in: ${field}`);
        return false;
      }
    }

    const lat = parseFloat(formData.latitude);
    const lon = parseFloat(formData.longitude);
    if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      alert('Invalid coordinates');
      return false;
    }

    const price = parseFloat(formData.price);
    if (isNaN(price) || price <= 0) {
      alert('Please enter a valid price');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const input = {
        email: userEmail || user?.attributes?.email || '',
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        price: parseFloat(formData.price),
        date: formData.date,
        property_type: formData.property_type,
        total_floor_area: formData.total_floor_area ? parseFloat(formData.total_floor_area) : null,
        tenure: formData.tenure || '',
        new_build: formData.new_build
      };

      const response = await client.graphql(
        graphqlOperation(submitPropertyTransaction, { input })
      );

      if (response.data.submitPropertyTransaction.success) {
        setSubmitStatus({ type: 'success', message: 'Transaction submitted successfully!' });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus({
          type: 'error',
          message: response.data.submitPropertyTransaction.message || 'Submission failed'
        });
      }
    } catch (error) {
      console.error('Submission error:', error?.message || 'unknown');
      setSubmitStatus({
        type: 'error',
        message: 'Error submitting transaction. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Submit Property Transaction</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="submission-form">
          <div className="form-row">
            <div className="form-group">
              <label>Latitude *</label>
              <input
                type="number"
                step="any"
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
                placeholder="51.5074"
                required
              />
            </div>

            <div className="form-group">
              <label>Longitude *</label>
              <input
                type="number"
                step="any"
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
                placeholder="-0.1278"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Price (£) *</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                placeholder="500000"
                required
              />
            </div>

            <div className="form-group">
              <label>Transaction Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Property Type *</label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleInputChange}
                required
              >
                <option value="Detached">Detached</option>
                <option value="Semi-Detached">Semi-Detached</option>
                <option value="Terraced">Terraced</option>
                <option value="Flat">Flat</option>
              </select>
            </div>

            <div className="form-group">
              <label>Floor Area (sq m)</label>
              <input
                type="number"
                name="total_floor_area"
                value={formData.total_floor_area}
                onChange={handleInputChange}
                placeholder="100"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Tenure</label>
              <select
                name="tenure"
                value={formData.tenure}
                onChange={handleInputChange}
              >
                <option value="">Select...</option>
                <option value="Freehold">Freehold</option>
                <option value="Leasehold">Leasehold</option>
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="new_build"
                  checked={formData.new_build}
                  onChange={handleInputChange}
                />
                New Build
              </label>
            </div>
          </div>

          {submitStatus && (
            <div className={`submit-status ${submitStatus.type}`}>
              {submitStatus.message}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn" disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Transaction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SubmissionModal;
